import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  signInWithEmailAndPassword,
  signInWithEmailLink,
  isSignInWithEmailLink,
  updatePassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import type { AdminUser, AuthState, Credentials, AdminRole, AdminPermissions } from '@/types/auth';
import type { Merchant } from '@/types/merchant';
import type { DocumentData } from 'firebase/firestore';
import { setAnalyticsProperties, setAnalyticsUser } from '@/services/client-analytics';

interface AuthStore extends AuthState {
  // Merchant context
  currentMerchant: Merchant | null;
  isGlobalView: boolean;
  
  // Actions
  signIn: (credentials: Credentials) => Promise<void>;
  signInWithLink: (email: string, link: string) => Promise<{ requiresPasswordSetup: boolean }>;
  checkEmailLink: (url: string) => boolean;
  setPassword: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshUserToken: () => Promise<void>;
  setUser: (user: AdminUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentMerchant: (merchant: Merchant | null) => void;
  setGlobalView: (isGlobal: boolean) => void;
  initializeAuth: () => () => void;
}

// Helper function to extract permissions from custom claims
const getPermissionsFromClaims = (claims: Record<string, unknown>): AdminPermissions => {
  const role = claims?.role as AdminRole | undefined;
  const merchantIds = claims?.merchantIds as string[] | undefined;
  
  // Default permissions object
  const defaultPermissions: AdminPermissions = {
    canViewAllMerchants: false,
    canEditAllMerchants: false,
    canViewMerchantIds: [],
    canEditMerchantIds: [],
    canAccessAnalytics: false,
    canManageAdmins: false,
    canManageBilling: false,
    canViewPayouts: false,
  };
  
  if (!role) {
    return defaultPermissions;
  }
  
  return {
    canViewAllMerchants: role === 'bevpass_admin',
    canEditAllMerchants: role === 'bevpass_admin',
    canViewMerchantIds: merchantIds || [],
    canEditMerchantIds: role === 'merchant_owner' ? (merchantIds || []) : [],
    canAccessAnalytics: true,
    canManageAdmins: role === 'bevpass_admin',
    canManageBilling: role === 'bevpass_admin' || role === 'merchant_owner',
    canViewPayouts: role === 'bevpass_admin' || role === 'merchant_owner' || role === 'merchant_viewer',
  };
};

// Helper function to convert Firebase user to AdminUser
const firebaseUserToAdminUser = async (firebaseUser: FirebaseUser): Promise<AdminUser> => {
  const tokenResult = await firebaseUser.getIdTokenResult();
  const claims = tokenResult.claims as DocumentData;
  
  // Debug: Log claims to see what's available
  console.log('Firebase claims:', claims);
  
  // Check for role in different possible locations
  let role: AdminRole | null = null;
  
  // Check standard role claim
  if (claims.role) {
    role = claims.role as AdminRole;
  }
  // Check for admin flag (old BevPass setup)
  else if (claims.admin === true || claims.bevpass_admin === true) {
    role = 'bevpass_admin';
  }
  // Check for isBevPassAdmin flag
  else if (claims.isBevPassAdmin === true) {
    role = 'bevpass_admin';
  }
  // Do not infer admin from email. Only custom claims drive admin access.
  // If the user needs admin, grant via admins/{uid} or admin console.
  
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    role,
    merchantIds: claims.merchantIds as string[] | undefined,
    applicationId: claims.applicationId as string | undefined,
    permissions: getPermissionsFromClaims({ ...claims, role }),
  };
};

const MERCHANT_ROLES: AdminRole[] = ['merchant_owner', 'merchant_viewer'];

const resolveMerchantRoleFromUserProfile = async (
  firebaseUser: FirebaseUser,
  adminUser: AdminUser
): Promise<AdminUser | null> => {
  try {
    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
    const db = getFirestore();
    const maxAttempts = 3;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data() as DocumentData | undefined;
        const roleFromDoc = data?.role as AdminRole | undefined;

        if (roleFromDoc && MERCHANT_ROLES.includes(roleFromDoc)) {
          const merchantIdsFromDoc = Array.isArray(data?.merchantIds)
            ? (data.merchantIds as string[])
            : [];
          const applicationIdFromDoc =
            typeof data?.applicationId === 'string'
              ? (data.applicationId as string)
              : adminUser.applicationId;

          return {
            ...adminUser,
            role: roleFromDoc,
            merchantIds: merchantIdsFromDoc,
            applicationId: applicationIdFromDoc,
            permissions: getPermissionsFromClaims({
              role: roleFromDoc,
              merchantIds: merchantIdsFromDoc,
            }),
          };
        }
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  } catch (error) {
    console.warn('Failed to resolve merchant role from user profile', error);
  }

  return null;
};

const syncAnalyticsContext = (user: AdminUser | null, options?: { isGlobalView?: boolean }) => {
  void setAnalyticsUser(user?.uid ?? null);

  const isGlobalView = options?.isGlobalView ?? false;
  const role = user?.role ?? 'unknown';
  const merchantCount = Array.isArray(user?.merchantIds) ? user!.merchantIds!.length : 0;

  void setAnalyticsProperties({
    admin_role: user ? role : 'signed_out',
    admin_global_view: String(Boolean(user && isGlobalView)),
    admin_merchant_count: user ? String(merchantCount) : '0',
  });
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        loading: false,
        error: null,
        initialized: false,
        currentMerchant: null,
        isGlobalView: false, // Default to false, will be set based on role

        // Actions
        signIn: async (credentials: Credentials) => {
          set({ loading: true, error: null });
          try {
            const userCredential = await signInWithEmailAndPassword(
              auth,
              credentials.email,
              credentials.password
            );
            
            let adminUser = await firebaseUserToAdminUser(userCredential.user);
            
            // Fallback: if no role in claims yet, read admins/{uid}
            if (!adminUser.role) {
              try {
                const { getFirestore, doc, getDoc } = await import('firebase/firestore');
                const db = getFirestore();
                const adminDoc = await getDoc(doc(db, 'admins', userCredential.user.uid));
                if (adminDoc.exists()) {
                  const data = adminDoc.data() as DocumentData | undefined;
                  const roleFromDoc = data?.role as AdminRole | undefined;
                  const merchantIdsFromDoc = Array.isArray(data?.merchantIds) ? (data.merchantIds as string[]) : [];
                  if (roleFromDoc) {
                    // Force token refresh in background; proceed with role from doc
                    userCredential.user.getIdToken(true).catch(() => {});
                    adminUser = {
                      ...adminUser,
                      role: roleFromDoc,
                      merchantIds: merchantIdsFromDoc,
                      permissions: getPermissionsFromClaims({ role: roleFromDoc, merchantIds: merchantIdsFromDoc })
                    };
                  }
                }
              } catch (claimError) {
                console.warn('Failed to load admin doc fallback', claimError);
              }
            }

            if (!adminUser.role) {
              const merchantUser = await resolveMerchantRoleFromUserProfile(userCredential.user, adminUser);
              if (merchantUser) {
                adminUser = merchantUser;
              }
            }

            if (!adminUser.role) {
              await firebaseSignOut(auth);
              throw new Error('Access denied. You do not have admin privileges.');
            }
            
            // Set context based on role
            const isAdmin = adminUser.role === 'bevpass_admin';
            
            set({ 
              user: adminUser, 
              loading: false, 
              error: null,
              isGlobalView: isAdmin, // Only admins get global view
              currentMerchant: null // Will be set by merchant auto-selection
            });
            syncAnalyticsContext(adminUser, { isGlobalView: isAdmin });
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to sign in';
            set({ user: null, loading: false, error: message });
            throw error;
          }
        },

        signInWithLink: async (email: string, link: string) => {
          set({ loading: true, error: null });
          try {
            const userCredential = await signInWithEmailLink(auth, email, link);
            let adminUser = await firebaseUserToAdminUser(userCredential.user);
            
            // Check if password setup is required by looking at Firestore
            const { getFirestore, doc, getDoc } = await import('firebase/firestore');
            const db = getFirestore();
            
            // Check pending invitation for password setup requirement
            const invitationDoc = await getDoc(doc(db, 'pendingInvitations', userCredential.user.uid));
            let requiresPasswordSetup = false;
            
            if (invitationDoc.exists()) {
              const invitationData = invitationDoc.data() as DocumentData | undefined;
              requiresPasswordSetup = invitationData?.requiresPasswordSetup === true;
              
              // Update role from invitation if not set
              if (!adminUser.role && invitationData?.role) {
                adminUser.role = invitationData.role as AdminRole;
                adminUser.merchantIds = Array.isArray(invitationData.merchantIds) ? invitationData.merchantIds : [];
              }
            }
            
            if (!adminUser.role) {
              try {
                const { getFirestore, doc, getDoc } = await import('firebase/firestore');
                const db = getFirestore();
                const adminDoc = await getDoc(doc(db, 'admins', userCredential.user.uid));
                if (adminDoc.exists()) {
                  const data = adminDoc.data() as DocumentData | undefined;
                  const roleFromDoc = data?.role as AdminRole | undefined;
                  const merchantIdsFromDoc = Array.isArray(data?.merchantIds) ? (data.merchantIds as string[]) : [];
                  if (roleFromDoc) {
                    userCredential.user.getIdToken(true).catch(() => {});
                    adminUser = {
                      ...adminUser,
                      role: roleFromDoc,
                      merchantIds: merchantIdsFromDoc,
                      permissions: getPermissionsFromClaims({ role: roleFromDoc, merchantIds: merchantIdsFromDoc })
                    } as AdminUser;
                  }
                }
              } catch (claimError) {
                console.warn('Failed to load admin doc fallback', claimError);
              }
            }

            if (!adminUser.role) {
              const merchantUser = await resolveMerchantRoleFromUserProfile(userCredential.user, adminUser);
              if (merchantUser) {
                adminUser = merchantUser;
              }
            }

            if (!adminUser.role) {
              await firebaseSignOut(auth);
              throw new Error('Access denied. You do not have admin privileges.');
            }
            
            // Mark invitation as used
            if (invitationDoc.exists()) {
              const { updateDoc, serverTimestamp } = await import('firebase/firestore');
              await updateDoc(doc(db, 'pendingInvitations', userCredential.user.uid), {
                used: true,
                usedAt: serverTimestamp()
              });
            }
            
            // Update user document status
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'users', userCredential.user.uid), {
              status: 'active',
              emailVerified: true,
              lastActive: serverTimestamp()
            });
            
            // Set context based on role
            const isAdmin = adminUser.role === 'bevpass_admin';
            
            set({ 
              user: adminUser, 
              loading: false, 
              error: null,
              isGlobalView: isAdmin,
              currentMerchant: null
            });
            syncAnalyticsContext(adminUser, { isGlobalView: isAdmin });
            
            return { requiresPasswordSetup };
          } catch (error: unknown) {
            set({ 
              user: null, 
              loading: false, 
              error: error.message || 'Failed to sign in with email link' 
            });
            throw error;
          }
        },

        checkEmailLink: (url: string) => {
          return isSignInWithEmailLink(auth, url);
        },

        setPassword: async (password: string) => {
          const currentUser = auth.currentUser;
          if (!currentUser) {
            throw new Error('No user is currently signed in');
          }
          
          try {
            // Update password in Firebase Auth
            await updatePassword(currentUser, password);
            
            // Call Cloud Function to update user metadata
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const setPasswordFunc = httpsCallable(functions, 'setPasswordAfterEmailSignIn');
            await setPasswordFunc({ password });
            
            // Refresh user data
            const adminUser = await firebaseUserToAdminUser(currentUser);
            set({ user: adminUser });
          } catch (error: unknown) {
            throw new Error(error.message || 'Failed to set password');
          }
        },

        signOut: async () => {
          set({ loading: true });
          try {
            await firebaseSignOut(auth);
            set({ 
              user: null, 
              loading: false, 
              error: null,
              currentMerchant: null,
              isGlobalView: true
            });
            syncAnalyticsContext(null, { isGlobalView: false });
          } catch (error: unknown) {
            set({ 
              loading: false, 
              error: error.message || 'Failed to sign out' 
            });
            throw error;
          }
        },

        refreshToken: async () => {
          const currentUser = auth.currentUser;
          if (currentUser) {
            await currentUser.getIdToken(true);
            let adminUser = await firebaseUserToAdminUser(currentUser);
            if (!adminUser.role) {
              const merchantUser = await resolveMerchantRoleFromUserProfile(currentUser, adminUser);
              if (merchantUser) {
                adminUser = merchantUser;
              }
            }
            set({ user: adminUser });
            syncAnalyticsContext(adminUser, { isGlobalView: get().isGlobalView });
          }
        },
        
        refreshUserToken: async () => {
          const currentUser = auth.currentUser;
          if (currentUser) {
            // Force token refresh to get updated custom claims
            await currentUser.getIdToken(true);
            let adminUser = await firebaseUserToAdminUser(currentUser);
            if (!adminUser.role) {
              const merchantUser = await resolveMerchantRoleFromUserProfile(currentUser, adminUser);
              if (merchantUser) {
                adminUser = merchantUser;
              }
            }
            
            // Update the user in the store
            set({ user: adminUser });
            syncAnalyticsContext(adminUser, { isGlobalView: get().isGlobalView });
            
            // Reset merchant context for merchant users
            const isMerchantUser =
              adminUser.role === 'merchant_owner' || adminUser.role === 'merchant_viewer';
            if (isMerchantUser) {
              // Clear current merchant to force reload in AuthProvider
              set({ currentMerchant: null });
            }
          }
        },

        setUser: (user) => {
          set({ user });
          syncAnalyticsContext(user, { isGlobalView: get().isGlobalView });
        },
        setLoading: (loading) => set({ loading }),
        setError: (error) => set({ error }),
        
        setCurrentMerchant: (merchant) => set({ 
          currentMerchant: merchant,
          isGlobalView: !merchant 
        }),
        
        setGlobalView: (isGlobal) => set({ 
          isGlobalView: isGlobal,
          currentMerchant: isGlobal ? null : get().currentMerchant
        }),

        initializeAuth: () => {
          // Prevent multiple listeners
          if (get().initialized) {
            return () => {};
          }

          set({ initialized: true });
          
          // Set up auth state listener
          const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
              try {
                let adminUser = await firebaseUserToAdminUser(firebaseUser);
                
                if (!adminUser.role) {
                  try {
                    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
                    const db = getFirestore();
                    const adminDoc = await getDoc(doc(db, 'admins', firebaseUser.uid));
                    if (adminDoc.exists()) {
                      const data = adminDoc.data() as DocumentData | undefined;
                      const roleFromDoc = data?.role as AdminRole | undefined;
                      const merchantIdsFromDoc = Array.isArray(data?.merchantIds) ? (data.merchantIds as string[]) : [];
                      if (roleFromDoc) {
                        firebaseUser.getIdToken(true).catch(() => {});
                        adminUser = {
                          ...adminUser,
                          role: roleFromDoc,
                          merchantIds: merchantIdsFromDoc,
                          permissions: getPermissionsFromClaims({ role: roleFromDoc, merchantIds: merchantIdsFromDoc })
                        } as AdminUser;
                      }
                    }
                  } catch (claimError) {
                console.warn('Failed to load admin doc fallback', claimError);
              }
                }

                if (!adminUser.role) {
                  const merchantUser = await resolveMerchantRoleFromUserProfile(firebaseUser, adminUser);
                  if (merchantUser) {
                    adminUser = merchantUser;
                  }
                }

                if (!adminUser.role) {
                  await firebaseSignOut(auth);
                  set({ 
                    user: null, 
                    loading: false,
                    error: 'Access denied. You do not have admin privileges.' 
                  });
                  return;
                }
                
                // Set context based on role
                        const isAdmin = adminUser.role === 'bevpass_admin';
                
                set({ 
                  user: adminUser, 
                  loading: false,
                  error: null,
                  isGlobalView: isAdmin, // Only admins get global view
                  currentMerchant: null // Will be set by merchant auto-selection
                });
                syncAnalyticsContext(adminUser, { isGlobalView: isAdmin });
                
                // For merchant users, we'll auto-select their merchant
                // This will be handled by a separate function after this

                // Listen for admins/{uid} changes to auto-refresh claims
                try {
                  const { getFirestore, doc, onSnapshot } = await import('firebase/firestore');
                  const db = getFirestore();
                  const adminDocRef = doc(db, 'admins', firebaseUser.uid);
                  onSnapshot(adminDocRef, async (snap) => {
                    if (!snap.exists()) return;
                    try {
                      await firebaseUser.getIdToken(true);
                      const refreshed = await firebaseUserToAdminUser(firebaseUser);
                      set({ user: refreshed });
                    } catch (e) {
                      console.warn('Failed to refresh token after admins doc update', e);
                    }
                  });
                } catch (e) {
                  console.warn('Admin claims auto-refresh listener failed to attach', e);
                }
              } catch (error: unknown) {
                set({ 
                  user: null, 
                  loading: false,
                  error: error.message 
                });
              }
            } else {
              set({ 
                user: null, 
                loading: false,
                error: null 
              });
              syncAnalyticsContext(null, { isGlobalView: false });
            }
          });

          // Return cleanup function
          return unsubscribe;
        },
      }),
      {
        name: 'bevpass-auth-v2',
        // Persist user data and merchant context
        partialize: (state) => ({ 
          user: state.user,
          currentMerchant: state.currentMerchant,
          isGlobalView: state.isGlobalView
        }),
      }
    )
  )
);
